import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";

import { TextField } from "../../components/TextField/TextField";
import { useSaveProperty } from "../../hooks/useSaveProperty";
import { useScreenSize } from "../../hooks/useScreenSize";
import { propertyManagementActions } from "../../store/property-management-slice";
import { OpeningDay } from "../../types/property";
import { CustomButton } from "../CustomButton/CustomButton";
import { Info } from "../Info/Info";
import { OpeningDays } from "../OpeningDays/OpeningDays";

import styles from "./property-form.module.css";

interface PropertyFormProps {
  isOpen: boolean;
  onClose: () => void;
  onPropertyCreatedOrUpdated?: () => void;
}

export const PropertyForm = ({
  isOpen,
  onClose,
  onPropertyCreatedOrUpdated,
}: PropertyFormProps) => {
  const dispatch = useDispatch();
  const { isDesktop } = useScreenSize();
  const property = useSelector(
    (state: any) => state.propertyManagement.property
  );
  
  const { saveProperty, updateProperty, isSaving, error, setError } =
    useSaveProperty();

  const resetAndClose = () => {
    onClose();
    dispatch(propertyManagementActions.resetState());
    setError("");
  };

  const closeModal = () => {
    if (isSaving) return;

    resetAndClose();
  };

  const hasInvalidOpeningHours = () =>
    property.openingHours?.some(
      (day: OpeningDay) =>
        day.isOpen &&
        (!day.openTime || !day.closeTime || day.closeTime <= day.openTime)
    ) ?? false;

  const onSaveProperty = async () => {
    setError("");

    if (hasInvalidOpeningHours()) {
      setError(
        "Die Schließzeit muss nach der Öffnungszeit liegen. Bitte überprüfen Sie die Öffnungszeiten."
      );
      return;
    }

    try {
      if (property.id && !property.isNewProperty) await updateProperty();
      else if (property.isNewProperty) await saveProperty();
      else await saveProperty();
    } catch {
      return;
    }

    onPropertyCreatedOrUpdated?.();
    resetAndClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={closeModal}>
      <div
        className={styles.popup}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles["title-row"]}>
          <span className={styles["title"]}>{t("AddNewObject")}</span>
          <button className={styles["close-button"]} onClick={closeModal}>
            &times;
          </button>
        </div>
        {error && <Info type="error">{error}</Info>}
        <div className={styles["popup-content"]}>
          <TextField
            label="Name"
            placeholder={property.name1 ? property.name1 : "Name"}
            value={property.name1}
            onChange={(e) =>
              dispatch(
                propertyManagementActions.setPropertyName(e.target.value)
              )
            }
            customStyles={{ marginBottom: isDesktop ? "12px" : "10px" }}
            inputStyles={{ maxWidth: "100%" }}
          />

          <TextField
            label="Zusatz"
            placeholder={property.name2 ? property.name2 : "Zusatz"}
            value={property.name2 || ""}
            onChange={(e) =>
              dispatch(
                propertyManagementActions.setAdditionalName(e.target.value)
              )
            }
            customStyles={{ marginBottom: isDesktop ? "12px" : "10px" }}
            inputStyles={{ maxWidth: "100%" }}
          />

          <TextField
            label="Straße"
            placeholder={property.street ? property.street : "Straße"}
            value={property.street}
            onChange={(e) =>
              dispatch(propertyManagementActions.setStreet(e.target.value))
            }
            customStyles={{ marginBottom: isDesktop ? "12px" : "10px" }}
            inputStyles={{ maxWidth: "100%" }}
          />

          <div className={styles["zip-and-city"]}>
            <TextField
              label="Plz"
              placeholder={property.postcode ? property.postcode : "Plz"}
              value={property.postcode}
              onChange={(e) =>
                dispatch(propertyManagementActions.setPostcode(e.target.value))
              }
              customStyles={{ width: "106px" }}
            />

            <TextField
              label="Stadt"
              placeholder={property.city ? property.city : "Stadt"}
              value={property.city}
              onChange={(e) =>
                dispatch(propertyManagementActions.setCity(e.target.value))
              }
              inputStyles={{ maxWidth: "100%" }}
            />
          </div>

          <OpeningDays
            value={property.openingHours ?? []}
            onChange={(hours) =>
              dispatch(propertyManagementActions.setOpeningHours(hours))
            }
          />

          <TextField
            label="Bemerkungen"
            placeholder={property.remark ? property.remark : "Bemerkungen"}
            value={property.remark || ""}
            onChange={(e) =>
              dispatch(propertyManagementActions.setRemark(e.target.value))
            }
            customStyles={{ marginBottom: "16px" }}
            asTextArea
            inputStyles={{
              maxWidth: "100%",
              minHeight: isDesktop ? "118px" : "101px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div className={styles.buttons}>
          <CustomButton
            color="white"
            onClick={closeModal}
            width={isDesktop ? 20 : 40}
          >
            {t("Cancel")}
          </CustomButton>

          <CustomButton
            loading={isSaving}
            disabled={!property.name1 || !property.customerId || isSaving}
            color="green"
            onClick={onSaveProperty}
            width={isDesktop ? 20 : 40}
          >
            Speichern
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
